import React, { useState, useEffect, useCallback } from 'react';
import apiService from '../../services/api';
import type { InstagramCampaign, BudgetSummary, BrandAsset } from './types';
import { STATUS_STYLES, formatStatus } from './types';

const OBJECTIVES = [
  { value: 'AWARENESS', label: 'Awareness' },
  { value: 'TRAFFIC', label: 'Traffic' },
  { value: 'ENGAGEMENT', label: 'Engagement' },
  { value: 'LEADS', label: 'Leads' },
  { value: 'SALES', label: 'Sales' },
];

const CTA_OPTIONS = [
  { value: 'SHOP_NOW', label: 'Shop Now' },
  { value: 'LEARN_MORE', label: 'Learn More' },
  { value: 'ORDER_NOW', label: 'Order Now' },
  { value: 'SIGN_UP', label: 'Sign Up' },
];

const CampaignsComponent: React.FC = () => {
  const [campaigns, setCampaigns] = useState<InstagramCampaign[]>([]);
  const [budget, setBudget] = useState<BudgetSummary | null>(null);
  const [assets, setAssets] = useState<BrandAsset[]>([]);
  const [showForm, setShowForm] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [isGenerating, setIsGenerating] = useState(false);
  const [message, setMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);

  const [form, setForm] = useState({
    name: '',
    objective: 'TRAFFIC',
    totalBudget: '',
    dailyBudget: '',
    startDate: '',
    endDate: '',
    ageMin: '21',
    ageMax: '45',
    interests: '',
    locations: 'US',
    headline: '',
    body: '',
    callToAction: 'SHOP_NOW',
    mediaUrl: '',
    linkUrl: 'https://waddleandsip.com/preorder',
  });

  const notify = (type: 'success' | 'error', text: string) => {
    setMessage({ type, text });
    setTimeout(() => setMessage(null), 5000);
  };

  const loadData = useCallback(async () => {
    setIsLoading(true);
    try {
      const [campaignsRes, budgetRes, assetsRes] = await Promise.all([
        apiService.getInstagramCampaigns(),
        apiService.getCampaignBudgetSummary(),
        apiService.getBrandAssets(),
      ]);

      setCampaigns((campaignsRes as unknown as { campaigns: InstagramCampaign[] }).campaigns || []);
      setBudget((budgetRes as unknown as { budget: BudgetSummary }).budget);
      setAssets((assetsRes as unknown as { assets: BrandAsset[] }).assets || []);
    } catch (err) {
      notify('error', err instanceof Error ? err.message : 'Failed to load campaigns');
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const updateForm = (field: string, value: string) => {
    setForm((prev) => ({ ...prev, [field]: value }));
  };

  const handleGenerateCopy = async () => {
    setIsGenerating(true);
    try {
      const audience = `ages ${form.ageMin}-${form.ageMax}${form.interests ? `, interested in ${form.interests}` : ''}`;
      const res = await apiService.generateCampaignCopy(form.objective, audience) as unknown as {
        headline: string; body: string; callToAction: string;
      };

      setForm((prev) => ({
        ...prev,
        headline: res.headline,
        body: res.body,
        callToAction: res.callToAction,
      }));
      notify('success', 'Ad copy generated');
    } catch (err) {
      notify('error', err instanceof Error ? err.message : 'Generation failed');
    } finally {
      setIsGenerating(false);
    }
  };

  const handleCreate = async () => {
    if (!form.name.trim()) {
      notify('error', 'A campaign name is required');
      return;
    }

    setIsSaving(true);
    try {
      await apiService.createInstagramCampaign({
        name: form.name,
        objective: form.objective,
        totalBudget: Number(form.totalBudget) || 0,
        budget: {
          daily: Number(form.dailyBudget) || undefined,
          currency: 'USD',
        },
        startDate: form.startDate || undefined,
        endDate: form.endDate || undefined,
        targeting: {
          ageMin: Number(form.ageMin),
          ageMax: Number(form.ageMax),
          interests: form.interests.split(',').map((s) => s.trim()).filter(Boolean),
          locations: form.locations.split(',').map((s) => s.trim()).filter(Boolean),
        },
        adCreative: {
          headline: form.headline,
          body: form.body,
          callToAction: form.callToAction,
          mediaUrl: form.mediaUrl,
          linkUrl: form.linkUrl,
        },
      });

      notify('success', 'Campaign draft created');
      setShowForm(false);
      setForm((prev) => ({ ...prev, name: '', totalBudget: '', dailyBudget: '', headline: '', body: '' }));
      loadData();
    } catch (err) {
      notify('error', err instanceof Error ? err.message : 'Create failed');
    } finally {
      setIsSaving(false);
    }
  };

  const handleAction = async (
    action: 'launch' | 'pause' | 'resume' | 'sync' | 'delete',
    campaignId: string,
  ) => {
    try {
      const actions = {
        launch: () => apiService.launchInstagramCampaign(campaignId),
        pause: () => apiService.pauseInstagramCampaign(campaignId),
        resume: () => apiService.resumeInstagramCampaign(campaignId),
        sync: () => apiService.syncCampaignPerformance(campaignId),
        delete: () => apiService.deleteInstagramCampaign(campaignId),
      };

      await actions[action]();
      notify('success', `Campaign ${action === 'sync' ? 'synced' : `${action}d`}`);
      loadData();
    } catch (err) {
      notify('error', err instanceof Error ? err.message : `${action} failed`);
    }
  };

  const budgetUsedPct = budget?.monthlyCap
    ? Math.min((budget.allocated / budget.monthlyCap) * 100, 100)
    : 0;

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-16">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-brand-blue" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {message && (
        <div className={`rounded-xl px-6 py-4 font-body ${
          message.type === 'success'
            ? 'bg-scarf-green/10 border border-scarf-green/30 text-scarf-green'
            : 'bg-brand-red/10 border border-brand-red/30 text-brand-red'
        }`}>
          {message.text}
        </div>
      )}

      {/* Budget */}
      <div className="bg-white rounded-2xl p-6 border border-ice-teal-light shadow-sm">
        <h3 className="text-xl font-title font-bold text-deep-navy mb-4">Monthly Budget</h3>

        <div className="grid md:grid-cols-4 gap-4 mb-4">
          <div className="bg-frost-white rounded-xl p-4">
            <p className="text-sm text-gray-600 font-body">Cap</p>
            <p className="text-2xl font-bold text-deep-navy">${budget?.monthlyCap.toFixed(2)}</p>
          </div>
          <div className="bg-frost-white rounded-xl p-4">
            <p className="text-sm text-gray-600 font-body">Allocated</p>
            <p className="text-2xl font-bold text-brand-blue">${budget?.allocated.toFixed(2)}</p>
          </div>
          <div className="bg-frost-white rounded-xl p-4">
            <p className="text-sm text-gray-600 font-body">Spent</p>
            <p className="text-2xl font-bold text-brand-red">${budget?.spent.toFixed(2)}</p>
          </div>
          <div className="bg-frost-white rounded-xl p-4">
            <p className="text-sm text-gray-600 font-body">Remaining</p>
            <p className="text-2xl font-bold text-scarf-green">${budget?.remaining.toFixed(2)}</p>
          </div>
        </div>

        <div className="w-full bg-frost-white rounded-full h-3 overflow-hidden">
          <div
            className="bg-brand-blue h-full rounded-full transition-all duration-500"
            style={{ width: `${budgetUsedPct}%` }}
          />
        </div>
      </div>

      <button
        onClick={() => setShowForm(!showForm)}
        className="bg-brand-blue hover:bg-brand-blue-dark text-white font-bold px-6 py-3 rounded-xl transition-colors"
      >
        {showForm ? 'Cancel' : 'New Campaign'}
      </button>

      {/* Create form */}
      {showForm && (
        <div className="bg-white rounded-2xl p-6 border border-ice-teal-light shadow-sm space-y-4">
          <h3 className="text-xl font-title font-bold text-deep-navy">Create Campaign</h3>

          <div className="grid md:grid-cols-2 gap-4">
            <div>
              <label htmlFor="c-name" className="block text-sm font-bold text-deep-navy mb-2 font-body">Name</label>
              <input
                id="c-name" type="text" value={form.name}
                onChange={(e) => updateForm('name', e.target.value)}
                placeholder="Preorder push - September"
                className="w-full px-4 py-3 rounded-xl border-2 border-ice-teal-light bg-frost-white text-deep-navy focus:border-brand-blue outline-none font-body"
              />
            </div>
            <div>
              <label htmlFor="c-objective" className="block text-sm font-bold text-deep-navy mb-2 font-body">Objective</label>
              <select
                id="c-objective" value={form.objective}
                onChange={(e) => updateForm('objective', e.target.value)}
                className="w-full px-4 py-3 rounded-xl border-2 border-ice-teal-light bg-frost-white text-deep-navy focus:border-brand-blue outline-none font-body"
              >
                {OBJECTIVES.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
              </select>
            </div>
            <div>
              <label htmlFor="c-total" className="block text-sm font-bold text-deep-navy mb-2 font-body">
                Total Budget (USD)
              </label>
              <input
                id="c-total" type="number" min="0" step="0.01" value={form.totalBudget}
                onChange={(e) => updateForm('totalBudget', e.target.value)}
                placeholder={`Max ${budget?.remaining.toFixed(2) ?? '0.00'}`}
                className="w-full px-4 py-3 rounded-xl border-2 border-ice-teal-light bg-frost-white text-deep-navy focus:border-brand-blue outline-none font-body"
              />
            </div>
            <div>
              <label htmlFor="c-daily" className="block text-sm font-bold text-deep-navy mb-2 font-body">
                Daily Budget (USD)
              </label>
              <input
                id="c-daily" type="number" min="0" step="0.01" value={form.dailyBudget}
                onChange={(e) => updateForm('dailyBudget', e.target.value)}
                className="w-full px-4 py-3 rounded-xl border-2 border-ice-teal-light bg-frost-white text-deep-navy focus:border-brand-blue outline-none font-body"
              />
            </div>
            <div>
              <label htmlFor="c-start" className="block text-sm font-bold text-deep-navy mb-2 font-body">Start Date</label>
              <input
                id="c-start" type="date" value={form.startDate}
                onChange={(e) => updateForm('startDate', e.target.value)}
                className="w-full px-4 py-3 rounded-xl border-2 border-ice-teal-light bg-frost-white text-deep-navy focus:border-brand-blue outline-none font-body"
              />
            </div>
            <div>
              <label htmlFor="c-end" className="block text-sm font-bold text-deep-navy mb-2 font-body">End Date</label>
              <input
                id="c-end" type="date" value={form.endDate}
                onChange={(e) => updateForm('endDate', e.target.value)}
                className="w-full px-4 py-3 rounded-xl border-2 border-ice-teal-light bg-frost-white text-deep-navy focus:border-brand-blue outline-none font-body"
              />
            </div>
          </div>

          <h4 className="text-lg font-title font-bold text-deep-navy pt-2">Targeting</h4>
          <div className="grid md:grid-cols-4 gap-4">
            <div>
              <label htmlFor="c-agemin" className="block text-sm font-bold text-deep-navy mb-2 font-body">Age Min</label>
              <input
                id="c-agemin" type="number" min="13" max="65" value={form.ageMin}
                onChange={(e) => updateForm('ageMin', e.target.value)}
                className="w-full px-4 py-3 rounded-xl border-2 border-ice-teal-light bg-frost-white text-deep-navy focus:border-brand-blue outline-none font-body"
              />
            </div>
            <div>
              <label htmlFor="c-agemax" className="block text-sm font-bold text-deep-navy mb-2 font-body">Age Max</label>
              <input
                id="c-agemax" type="number" min="13" max="65" value={form.ageMax}
                onChange={(e) => updateForm('ageMax', e.target.value)}
                className="w-full px-4 py-3 rounded-xl border-2 border-ice-teal-light bg-frost-white text-deep-navy focus:border-brand-blue outline-none font-body"
              />
            </div>
            <div className="md:col-span-2">
              <label htmlFor="c-interests" className="block text-sm font-bold text-deep-navy mb-2 font-body">
                Interests <span className="font-normal text-gray-500">(comma separated)</span>
              </label>
              <input
                id="c-interests" type="text" value={form.interests}
                onChange={(e) => updateForm('interests', e.target.value)}
                placeholder="craft cocktails, mocktails, sober curious"
                className="w-full px-4 py-3 rounded-xl border-2 border-ice-teal-light bg-frost-white text-deep-navy focus:border-brand-blue outline-none font-body"
              />
            </div>
          </div>

          <div className="flex justify-between items-center pt-2">
            <h4 className="text-lg font-title font-bold text-deep-navy">Ad Creative</h4>
            <button
              onClick={handleGenerateCopy}
              disabled={isGenerating}
              className="text-sm text-brand-blue hover:text-brand-blue-dark font-bold disabled:opacity-50"
            >
              {isGenerating ? 'Generating…' : 'Generate with AI'}
            </button>
          </div>

          <div className="grid md:grid-cols-2 gap-4">
            <div>
              <label htmlFor="c-headline" className="block text-sm font-bold text-deep-navy mb-2 font-body">
                Headline <span className="font-normal text-gray-500">({form.headline.length}/40)</span>
              </label>
              <input
                id="c-headline" type="text" maxLength={40} value={form.headline}
                onChange={(e) => updateForm('headline', e.target.value)}
                className="w-full px-4 py-3 rounded-xl border-2 border-ice-teal-light bg-frost-white text-deep-navy focus:border-brand-blue outline-none font-body"
              />
            </div>
            <div>
              <label htmlFor="c-cta" className="block text-sm font-bold text-deep-navy mb-2 font-body">Call to Action</label>
              <select
                id="c-cta" value={form.callToAction}
                onChange={(e) => updateForm('callToAction', e.target.value)}
                className="w-full px-4 py-3 rounded-xl border-2 border-ice-teal-light bg-frost-white text-deep-navy focus:border-brand-blue outline-none font-body"
              >
                {CTA_OPTIONS.map((c) => <option key={c.value} value={c.value}>{c.label}</option>)}
              </select>
            </div>
            <div className="md:col-span-2">
              <label htmlFor="c-body" className="block text-sm font-bold text-deep-navy mb-2 font-body">
                Body <span className="font-normal text-gray-500">({form.body.length}/125)</span>
              </label>
              <textarea
                id="c-body" maxLength={125} rows={3} value={form.body}
                onChange={(e) => updateForm('body', e.target.value)}
                className="w-full px-4 py-3 rounded-xl border-2 border-ice-teal-light bg-frost-white text-deep-navy focus:border-brand-blue outline-none font-body resize-none"
              />
            </div>
          </div>

          <div>
            <span className="block text-sm font-bold text-deep-navy mb-2 font-body">Ad Image</span>
            <div className="grid grid-cols-8 gap-2">
              {assets.map((asset) => (
                <button
                  key={asset.file}
                  onClick={() => updateForm('mediaUrl', asset.url)}
                  title={asset.description}
                  className={`aspect-square rounded-lg overflow-hidden border-2 transition-all ${
                    form.mediaUrl === asset.url
                      ? 'border-brand-blue ring-2 ring-brand-blue/30'
                      : 'border-ice-teal-light hover:border-ice-teal'
                  }`}
                >
                  <img src={asset.url} alt={asset.description} className="w-full h-full object-cover" />
                </button>
              ))}
            </div>
          </div>

          <button
            onClick={handleCreate}
            disabled={isSaving}
            className="w-full bg-brand-blue hover:bg-brand-blue-dark text-white font-bold px-6 py-3 rounded-xl transition-colors disabled:opacity-50"
          >
            {isSaving ? 'Creating…' : 'Create Draft Campaign'}
          </button>
        </div>
      )}

      {/* Campaign list */}
      <div className="bg-white rounded-2xl p-6 border border-ice-teal-light shadow-sm">
        <h3 className="text-xl font-title font-bold text-deep-navy mb-4">Campaigns</h3>

        {campaigns.length === 0 ? (
          <p className="text-gray-600 font-body py-8 text-center">
            No campaigns yet. Create one above to get started.
          </p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-ice-teal-light">
                  <th className="text-left py-3 px-4 text-deep-navy font-bold">Name</th>
                  <th className="text-left py-3 px-4 text-deep-navy font-bold">Status</th>
                  <th className="text-left py-3 px-4 text-deep-navy font-bold">Budget</th>
                  <th className="text-left py-3 px-4 text-deep-navy font-bold">Performance</th>
                  <th className="text-left py-3 px-4 text-deep-navy font-bold">Actions</th>
                </tr>
              </thead>
              <tbody>
                {campaigns.map((c) => (
                  <tr key={c._id} className="border-b border-gray-100 hover:bg-frost-white transition-colors">
                    <td className="py-3 px-4 font-body text-deep-navy font-bold">{c.name}</td>
                    <td className="py-3 px-4">
                      <span className={`px-3 py-1 rounded-full text-xs font-bold ${STATUS_STYLES[c.status] || 'bg-gray-100 text-gray-700'}`}>
                        {formatStatus(c.status)}
                      </span>
                    </td>
                    <td className="py-3 px-4 font-body text-gray-600 text-sm">
                      ${c.spent.toFixed(2)} / ${c.totalBudget.toFixed(2)}
                    </td>
                    <td className="py-3 px-4 font-body text-gray-600 text-sm">
                      {c.performance.impressions
                        ? `${c.performance.clicks} clicks · ${c.performance.ctr.toFixed(2)}% CTR`
                        : '—'}
                    </td>
                    <td className="py-3 px-4">
                      <div className="flex gap-2 flex-wrap">
                        {c.status === 'draft' && (
                          <button
                            onClick={() => handleAction('launch', c._id)}
                            className="text-scarf-green hover:opacity-70 font-bold text-sm"
                          >
                            Launch
                          </button>
                        )}
                        {c.status === 'active' && (
                          <button
                            onClick={() => handleAction('pause', c._id)}
                            className="text-yellow-700 hover:opacity-70 font-bold text-sm"
                          >
                            Pause
                          </button>
                        )}
                        {c.status === 'paused' && (
                          <button
                            onClick={() => handleAction('resume', c._id)}
                            className="text-scarf-green hover:opacity-70 font-bold text-sm"
                          >
                            Resume
                          </button>
                        )}
                        {c.metaCampaignId && (
                          <button
                            onClick={() => handleAction('sync', c._id)}
                            className="text-brand-blue hover:text-brand-blue-dark font-bold text-sm"
                          >
                            Sync
                          </button>
                        )}
                        {c.status !== 'active' && (
                          <button
                            onClick={() => handleAction('delete', c._id)}
                            className="text-brand-red hover:text-brand-red-light font-bold text-sm"
                          >
                            Delete
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};

export default CampaignsComponent;
